"use client"

import * as React from "react"

import { ArenaMark } from "@/components/arena/v2/primitives"
import { cn } from "@/lib/utils"

/* =============================================================================
   CreatorFieldCard — the Entries state card, in the creator identity.

   Same composition as the Arena lifecycle cards, on purpose: a strip on top,
   copy clamped left, one oversized figure pinned right, a rail under it, a foot
   bar below. That card is how this product says "one object, one state, one
   number that matters", and both places it is used here have exactly that job:
   the reader's own standing, and what a creator has at risk tonight.

   WHY A FORK AND NOT AN IMPORT. ArenaFieldCard's fields are an Arena palette,
   where pink is action, gold is a paid amount and green belongs to nothing.
   A creator is mint (--creator, the landing's own). Using it would mean adding
   a tone to lib/arena's table, and Arena is parked. So this takes the
   composition and none of the Arena colour rules. The brand mark is imported
   rather than re-inlined: one source of truth for the shape.

   Smaller than the original on purpose: no radar (nothing here is a search),
   no threshold tick (no gate to clear), no reveal beat.
   ============================================================================= */

export const FIELD = {
  /** The reader's own standing, and a creator with money on. Deep mint. */
  creator: {
    background:
      "linear-gradient(118deg, hsl(152 30% 5%) 0%, hsl(152 26% 9%) 55%, hsl(152 24% 13%) 100%)",
    glow: "radial-gradient(130% 180% at 84% 4%, hsl(var(--creator) / 0.26) 0%, transparent 52%)",
    bloom: "hsl(var(--creator) / 0.16)",
  },
  /** Nothing is at risk, nothing has happened. Near-black, no hue. */
  dark: {
    background:
      "linear-gradient(118deg, hsl(0 0% 3%) 0%, hsl(0 0% 6%) 55%, hsl(0 0% 9%) 100%)",
    glow: "radial-gradient(130% 180% at 84% 4%, hsl(var(--white-100)) 0%, transparent 52%)",
    bloom: "hsl(0 0% 100% / 0.05)",
  },
} as const

/** Type on the strip. Our two faces only: this is Open Sauce, not mono. */
const STRIP_TEXT =
  "font-open-sauce text-[10px] font-semibold uppercase tracking-[0.08em]"

export interface CreatorFieldCardProps extends React.ComponentProps<"div"> {
  tone?: keyof typeof FIELD
  strip: { left: string; right: React.ReactNode }
  /** Two or three words on the bottom baseline. Ends with a full stop. */
  headline: string
  /** The one sentence on the card. */
  caption?: string
  figure?: string
  figureCaption?: string
  progress?: { value: number; max: number; left?: React.ReactNode; right?: React.ReactNode }
  foot?: { left: React.ReactNode; right: React.ReactNode }
  /** Compact metrics: drops the figure size a step for a narrow column. */
  dense?: boolean
}

export function CreatorFieldCard({
  tone = "creator",
  strip,
  headline,
  caption,
  figure,
  figureCaption,
  progress,
  foot,
  dense = false,
  className,
  ...props
}: CreatorFieldCardProps) {
  const field = FIELD[tone]
  return (
    <div
      className={cn(
        "overflow-hidden rounded-[14px] ring-1 ring-inset ring-white-100",
        className
      )}
      {...props}
    >
      <div
        className={cn(
          "flex items-center justify-between gap-3 px-3 py-1.5",
          tone === "creator" ? "bg-creator text-grey-950" : "bg-grey-900 text-grey-300"
        )}
      >
        <span className={STRIP_TEXT}>{strip.left}</span>
        <span className={cn(STRIP_TEXT, "shrink-0")}>{strip.right}</span>
      </div>

      <div
        className="relative isolate w-full overflow-hidden"
        style={{ background: field.background }}
      >
        {/* Directional light: lit behind the figure, dark under the copy so the
            headline holds contrast against the brightest corner. */}
        <span
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{
            background: `${field.glow}, linear-gradient(118deg, hsl(var(--black-500)) 0%, transparent 42%)`,
          }}
        />
        {/* The board banners' halftone grid, same values as the Entries cards,
            so this and the lifecycle cards read as one printed surface. */}
        <span
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{
            backgroundImage:
              "radial-gradient(rgba(255,255,255,0.55) 1px, transparent 1.4px)",
            backgroundSize: "7px 7px",
            opacity: 0.16,
            mixBlendMode: "overlay",
            maskImage: "linear-gradient(to left, black 0%, transparent 80%)",
            WebkitMaskImage: "linear-gradient(to left, black 0%, transparent 80%)",
          }}
        />
        <span
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{
            background: `radial-gradient(120% 150% at 6% 100%, ${field.bloom} 0%, transparent 52%)`,
          }}
        />
        {/* The mark in the ground: material, not a watermark. Anchored from the
            top so growing it never pushes up into the strip. */}
        <ArenaMark
          className="pointer-events-none absolute -top-1 -right-[4%] aspect-[40.7/31] w-[52%] min-w-[150px]"
          alphaBoost={0.03}
        />

        <div className="relative">
          {figure && (
            <span
              aria-hidden
              className="pointer-events-none absolute right-3 top-1/2 flex -translate-y-1/2 flex-col items-end"
            >
              <span
                className="font-open-sauce font-black leading-none tracking-[-0.04em] tabular-nums text-white"
                style={{
                  fontSize: dense
                    ? `clamp(26px, 7vw, ${figure.length > 5 ? 30 : 38}px)`
                    : `clamp(34px, 10vw, ${figure.length > 6 ? 46 : figure.length > 4 ? 58 : 72}px)`,
                }}
              >
                {figure}
              </span>
              {figureCaption && (
                <span className="mt-1 font-open-sauce text-[11px] font-semibold tracking-tight text-white/55">
                  {figureCaption}
                </span>
              )}
            </span>
          )}

          <div
            className={cn(
              "relative z-[4] flex flex-col p-3",
              figure && (dense ? "min-h-[74px]" : "min-h-[110px]")
            )}
            style={{ maxWidth: figure ? "58%" : "100%" }}
          >
            <h2
              className="font-open-sauce font-black leading-[1.05] tracking-tight text-white"
              style={{ fontSize: dense ? "15px" : "clamp(17px, 3vw, 22px)" }}
            >
              {headline}
            </h2>
            {caption && (
              <p className="mt-1 font-open-sauce text-[11px] font-normal leading-snug text-white-700">
                {caption}
              </p>
            )}
            {/* The figure is artwork above; spoken once here so a screen reader
                gets the sentence and not a picture out of order. */}
            {figure && (
              <span className="sr-only">
                {figure}
                {figureCaption ? `, ${figureCaption}` : ""}
              </span>
            )}
          </div>
        </div>

        {progress && (
          <div className="relative z-[4] -mt-1 px-3 pb-2.5">
            <div className="h-[5px] w-full overflow-hidden rounded-full bg-white-100">
              <span
                className={cn(
                  "block h-full rounded-full",
                  tone === "creator" ? "bg-creator" : "bg-grey-500"
                )}
                style={{
                  width: `${Math.min(
                    100,
                    Math.max(0, (progress.value / Math.max(progress.max, 1)) * 100)
                  )}%`,
                }}
              />
            </div>
            {(progress.left || progress.right) && (
              <div className="mt-1.5 flex items-baseline justify-between gap-2 text-[11px] font-semibold tracking-tight">
                <span className="text-white">{progress.left}</span>
                <span className="ml-auto text-white-700">{progress.right}</span>
              </div>
            )}
          </div>
        )}
      </div>

      {foot && (
        <div
          className="flex items-center justify-between gap-2 border-t border-white-100 px-3 py-2"
          style={{ background: "hsl(0 0% 4%)" }}
        >
          <span className="text-[11px] leading-tight text-white-700">{foot.left}</span>
          <span className="shrink-0 text-[11px] font-semibold tracking-tight text-white">
            {foot.right}
          </span>
        </div>
      )}
    </div>
  )
}
